import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { useId, type ReactNode } from 'react';
import { cx } from '../../utils/cx';
import { Icon } from '../Icon/Icon';
import s from './Checkbox.module.css';

export type CheckboxProps = Omit<RadixCheckbox.CheckboxProps, 'children'> & {
  /** Visible label. Omit only if another element labels it (then pass aria-label). */
  label?: ReactNode;
  description?: ReactNode;
  size?: 'md' | 'lg';
};

export function Checkbox({
  label,
  description,
  size = 'md',
  id,
  className,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const cbId = id ?? autoId;
  const descId = description ? `${cbId}-desc` : undefined;
  const box = (
    <RadixCheckbox.Root
      id={cbId}
      className={cx(s.box, !label && className)}
      data-size={size}
      aria-describedby={descId}
      {...rest}
    >
      <RadixCheckbox.Indicator className={s.indicator}>
        {rest.checked === 'indeterminate' ? (
          <Icon icon={Minus} size="sm" />
        ) : (
          <Icon icon={Check} size="sm" />
        )}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );
  if (!label) return box;
  return (
    <div className={cx(s.row, className)}>
      {box}
      <div className={s.text}>
        <label htmlFor={cbId} className={s.label}>
          {label}
        </label>
        {description ? (
          <p id={descId} className={s.description}>
            {description}
          </p>
        ) : null}
      </div>
    </div>
  );
}
